/**
 * Message Item - Vitreous Support message bubble with read receipts
 */

import React from 'react';
import { Avatar, Dropdown, Typography } from 'antd';
import { CheckOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { PresenceIndicator } from './PresenceIndicator';
import dayjs from 'dayjs';
import './MessageItem.css';

const { Text } = Typography;

const getAttachmentUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const hostname = window.location.hostname.toLowerCase();
  const isProduction =
    hostname === 'patelhrms.com' ||
    hostname.includes('patelhrms.com') ||
    hostname === 'vitreous.support' ||
    hostname.includes('vitreous.support') ||
    hostname === 'www.vitreous.support';
  const API_ORIGIN = isProduction ? window.location.origin : 'http://localhost:5000';
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_ORIGIN}${path}`;
};

interface MessageItemProps {
  message: {
    id: string;
    channelId: string;
    userId: string;
    text: string;
    contentType: string;
    attachmentUrl?: string;
    attachmentName?: string;
    threadCount: number;
    createdAt: string;
    updatedAt?: string;
    isEdited?: boolean;
    reactions?: Array<{
      emoji: string;
      count: number;
      userIds: string[];
      hasUserReaction?: boolean;
    }>;
    user: { id: string; firstName: string; lastName: string; email: string };
  };
  showAvatar: boolean;
  userPresence?: { status: 'online' | 'away' | 'busy' | 'offline' };
  isRead?: boolean;
  onReply?: () => void;
  onEdit?: (messageId: string, currentText: string) => void;
  onDelete?: () => void;
  onPin?: () => void;
  onOpenThread?: (messageId: string) => void;
  onReaction?: (messageId: string, emoji: string) => void;
}

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  showAvatar,
  userPresence,
  isRead = false,
  onReply,
  onEdit,
  onDelete,
  onPin,
  onOpenThread,
  onReaction,
}) => {
  const { user } = useAuth();
  const isOwnMessage = user?.id === message.userId;

  const formatTime = (ts: string) => dayjs(ts).format('h:mm A');

  const ReadReceipt: React.FC = () => {
    if (!isOwnMessage) return null;
    return (
      <span className="message-read-receipt" title={isRead ? 'Read' : 'Sent'}>
        {isRead ? (
          <span className="receipt-double">
            <CheckOutlined /><CheckOutlined />
          </span>
        ) : (
          <CheckOutlined className="receipt-sent" />
        )}
      </span>
    );
  };

  const menuItems = [
    { key: 'reply', label: 'Reply', onClick: onReply },
    ...(isOwnMessage && onEdit
      ? [{ key: 'edit', label: 'Edit', onClick: () => onEdit(message.id, message.text) }]
      : []),
  ];

  return (
    <div
      className={`message-bubble-wrapper ${isOwnMessage ? 'own' : 'other'}`}
    >
      {!isOwnMessage && showAvatar && (
        <div className="message-bubble-avatar">
          <PresenceIndicator status={userPresence?.status || 'offline'} size="small">
            <Avatar size={28} icon={<UserOutlined />} />
          </PresenceIndicator>
        </div>
      )}
      <div className={`message-bubble-container ${isOwnMessage ? 'own' : 'other'}`}>
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['contextMenu']}
          placement={isOwnMessage ? 'bottomLeft' : 'bottomRight'}
        >
          <div className={`message-bubble ${isOwnMessage ? 'own' : 'other'}`}>
            {!isOwnMessage && showAvatar && (
              <Text className="message-sender-name">
                {message.user.firstName} {message.user.lastName}
              </Text>
            )}
            <div className="message-bubble-content">
              {message.text && <div className="message-text">{message.text}</div>}
              {message.attachmentUrl && (
                <div className="message-attachment">
                  <a
                    href={getAttachmentUrl(message.attachmentUrl) || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📎 {message.attachmentName || 'Attachment'}
                  </a>
                </div>
              )}
              <div className="message-meta">
                <span className="message-time">{formatTime(message.createdAt)}</span>
                {message.isEdited && (
                  <span className="message-edited"> (edited)</span>
                )}
                <ReadReceipt />
              </div>
            </div>
          </div>
        </Dropdown>
        {message.reactions && message.reactions.length > 0 && (
          <div className="message-reactions">
            {message.reactions.map((r, i) => (
              <button
                key={i}
                className={`reaction-btn ${r.hasUserReaction ? 'active' : ''}`}
                onClick={() => onReaction?.(message.id, r.emoji)}
              >
                <span>{r.emoji}</span>
                {r.count > 1 && <span className="reaction-count">{r.count}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
